/**
 * usePrimarySourcesDefaultInit Hook
 *
 * Handles source-toggle defaults for the source control UI:
 *
 * - **Library (team setting)** — when the team admin enables
 *   `default_library_enabled`, the "My library" toggle is pre-enabled for
 *   members on first load.
 *
 * Uses a persisted guard so defaults never override explicit user choices.
 */

import { libraryDefaultAppliedAtom, sourceControlToggleAtom, toggleMyLibraryAtom } from '@/atoms/sourceControlAtoms';;
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useTeamEvidenceEnforcement } from '@heidi/evidence';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

/**
 * Hook to initialize source-toggle defaults.
 * Call this in components that render the source control UI.
 */
export const usePrimarySourcesDefaultInit = (): void => {
  const { hasEvidencePro } = useEvidenceBillingInfo();
  const toggleState = useAtomValue(sourceControlToggleAtom);
  const toggleMyLibrary = useSetAtom(toggleMyLibraryAtom);
  const [libraryDefaultApplied, setLibraryDefaultApplied] = useAtom(libraryDefaultAppliedAtom);

  const { defaultLibraryEnabled } = useTeamEvidenceEnforcement();

  useEffect(() => {
    if (
      defaultLibraryEnabled &&
      hasEvidencePro &&
      !libraryDefaultApplied &&
      !toggleState.myLibrary
    ) {
      toggleMyLibrary(true);
      setLibraryDefaultApplied(true);
    }
  }, [
    defaultLibraryEnabled,
    hasEvidencePro,
    libraryDefaultApplied,
    toggleState.myLibrary,
    toggleMyLibrary,
    setLibraryDefaultApplied,
  ]);
};
